import * as React from 'react';

const FOCUSABLE_SELECTOR =
	'input:not([type="hidden"]):not(:disabled), textarea:not(:disabled), [role="combobox"], button:not(:disabled), [tabindex]:not([tabindex="-1"])';

/**
 * Focuses the control held by `containerRef` and, when `isEnabled`, does so once on mount. Two frames
 * are waited: the sidebar's own initial focus (its close button) lands one frame after the content is
 * mounted, and ours must come last. `preventScroll` keeps the panel from jumping. Nothing is focused
 * when the focus already sits inside the control. Returns the same focus gesture for manual use.
 */
const useFocusFieldControl = (
	containerRef: React.RefObject<HTMLDivElement | null>,
	isEnabled: boolean,
): (() => void) => {
	const focusControl = React.useCallback(() => {
		const container = containerRef.current;

		if (container === null || container.contains(document.activeElement)) {
			return;
		}

		const target =
			container.querySelector<HTMLElement>('input:checked:not(:disabled)') ??
			container.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);

		target?.focus({ preventScroll: true });
	}, [containerRef]);

	React.useEffect(() => {
		if (!isEnabled) {
			return undefined;
		}

		let secondFrame = 0;
		const firstFrame = window.requestAnimationFrame(() => {
			secondFrame = window.requestAnimationFrame(focusControl);
		});

		return () => {
			window.cancelAnimationFrame(firstFrame);
			window.cancelAnimationFrame(secondFrame);
		};
	}, [isEnabled, focusControl]);

	return focusControl;
};

export default useFocusFieldControl;
